import { useContext } from "react";
import CustomSelect, { optionType } from "../organism/CustomSelect";
import { ApplicationState } from "../../services/context/ApplicationStateContext";
import CustomText from "../atomic/CustomText";
import { useQueryClient } from "@tanstack/react-query";
import Button from "../organism/Button";
import useProfile from "../../services/utils/custom hooks/useProfile";
import usePhysiciansOfOrg from "../../services/utils/custom hooks/usePhysiciansOfOrg";
import { PopUpContext } from "../../services/context/popupContext";
import useDuplicatePatient from "../../services/utils/custom hooks/Patients/useDuplicatePatient";

const phyDefault = { label: "Select a clinician", value: "" };
const orgDefault = { label: "Select organization", value: "" };
const pathLabDefault = { label: "Select Path Lab", value: "" };

export function OrganizationChangeWarningContent({
  patientId,
  organizationId,
  onCancel,
  onProceedSuccess,
}: Readonly<{
  patientId: string;
  organizationId: string | number;
  onCancel: () => void;
  /** Called with the new duplicated patient id so the order is created for the duplicated patient. */
  onProceedSuccess: (newPatientId: string) => void;
}>): JSX.Element {
  const { setVisible } = useContext(PopUpContext);
  const { mutate: duplicatePatient, isPending: isDuplicating } = useDuplicatePatient();

  const handleProceed = () => {
    if (organizationId == null) return;
    duplicatePatient(
      { patientId, organizationId },
      {
        onSuccess: (data: { patientId?: string; data?: { patientId?: string } }) => {
          setVisible(false);
          const newPatientId = data?.patientId ?? data?.data?.patientId;
          if (newPatientId) {
            onProceedSuccess(newPatientId);
          } else {
            onProceedSuccess(patientId);
          }
        },
      }
    );
  };

  return (
    <div className="w-full">
      <CustomText
        text="This patient doesn't belong to the selected organization. By proceeding, the patient will be duplicated to the selected organization."
        fontWeight="font-normal"
        className="text-sm md:text-md mb-5"
      />
      <div className="flex justify-end gap-3">
        <Button
          textInformation={{ text: "Cancel", fontWeight: "font-semibold" }}
          buttonTheme="secondary"
          className="px-8"
          onClick={onCancel}
        />
        <Button
          textInformation={{ text: "Proceed", fontWeight: "font-semibold" }}
          buttonTheme="primary"
          className="px-8"
          loading={isDuplicating}
          disabled={isDuplicating}
          onClick={handleProceed}
        />
      </div>
    </div>
  );
}

function OrganizationAndClinicianSelection({
  handleNext,
  createPatientFlow,
  isPatientActionsCell,
  patientId,
  patientOrganizationId,
}: Readonly<{
  /** Called when proceeding. Pass the new patient id when user duplicated the patient to the selected org. */
  handleNext: (newPatientId?: string) => void;
  createPatientFlow?: boolean;
  isPatientActionsCell?: boolean;
  patientId?: string;
  patientOrganizationId?: string | number;
}>): JSX.Element {
  const {
    state: { selectedOrganization, selectedPathLab, selectedPhysician },
    dispatch,
  } = useContext(ApplicationState);
  const { setVisible, setPopUp, setPopUpTitle } = useContext(PopUpContext);
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const organisations: any = queryClient.getQueryData(["organizations"]);
  const pathLabs: any = queryClient.getQueryData(["pathLabs"]);
  const orgOptions =
    organisations?.data?.map((org: any) => ({
      label: org.name,
      value: org.id,
      address: org.address,
      zipcode: org.zipcode,
      state: org.state,
    })) ?? [];
  const pathLabOptions =
    pathLabs?.data?.map((org: any) => ({
      label: org.name,
      value: org.id,
      address: org.address,
      zipcode: org.zipcode,
      state: org.state,
    })) ?? [];
  const selectedOrg =
    orgOptions?.find(
      (org: any) => org?.value == selectedOrganization?.toString()
    ) ?? orgDefault;
  const selectedPathLaboratory =
    pathLabOptions.find(
      (pathLab: any) => pathLab?.value == selectedPathLab?.toString()
    ) ?? pathLabDefault;
  const { data: physicians } = usePhysiciansOfOrg();
  const phyOptions = physicians?.data?.map((phy: any) => ({
    label: phy.firstName + " " + phy.lastName,
    value: phy.id,
  })) ?? [];
  const selectedPhy =
    phyOptions?.find(
      (phy: any) => phy?.value == selectedPhysician?.toString()
    ) ?? phyDefault;
  const roles: string[] =
    queryClient.getQueryData<any>(["profile"])?.user?.roles ?? [];

  const isPLUPhysician =
    roles.includes("Physician") && roles.includes("PathLab User");
  const isPhysicianOnly =
    (roles.length === 1 && roles.includes("Physician")) || isPLUPhysician;

  function handleOrgSelect(option: optionType) {
    if (option?.value === "") {
      dispatch({ type: "SET_ORGANIZATION", payload: null });
      dispatch({ type: "SET_ORGANIZATION_DATA", payload: null });

      if (!isPhysicianOnly) {
        dispatch({ type: "SET_PHYSICIAN", payload: null });
        dispatch({ type: "SET_PHYSICIAN_DATA", payload: null });
      }

      return;
    }

    dispatch({ type: "SET_ORGANIZATION", payload: option?.value });
    dispatch({ type: "SET_ORGANIZATION_DATA", payload: option });
    if (!isPhysicianOnly) {
      dispatch({ type: "SET_PHYSICIAN", payload: null });
      dispatch({ type: "SET_PHYSICIAN_DATA", payload: null });
    }
  }

  function handlePathLabSelect(option: optionType) {
    if (option?.value === "") {
      dispatch({ type: "SET_PATHLAB", payload: null });
      return;
    }
    dispatch({ type: "SET_PATHLAB", payload: option?.value });
  }

  function handlePhySelect(option: optionType) {
    if (option?.value === "") {
      dispatch({ type: "SET_PHYSICIAN", payload: null });
      dispatch({ type: "SET_PHYSICIAN_DATA", payload: null });
      return;
    }
    dispatch({ type: "SET_PHYSICIAN", payload: option?.value });
    // find the full physician object from fetched data and dispatch it
    const fullPhysician = physicians?.data?.find((p: any) => String(p.id) === String(option?.value));
    dispatch({ type: "SET_PHYSICIAN_DATA", payload: fullPhysician ?? option });
  }

  function onNextClick() {
    const isOrgMismatch =
      isPatientActionsCell &&
      patientId &&
      patientOrganizationId != null &&
      String(selectedOrganization) !== String(patientOrganizationId);

    if (isOrgMismatch && selectedOrganization != null) {
      setPopUpTitle("Organization change");
      setPopUp(
        <OrganizationChangeWarningContent
          patientId={patientId}
          organizationId={selectedOrganization}
          onCancel={() => setVisible(false)}
          onProceedSuccess={(newPatientId) => handleNext(newPatientId)}
        />
      );
      setVisible(true);
    } else {
      handleNext();
    }
  }

  return (
    <div className="flex flex-col min-w-[20vw]">
      {profile?.user?.roles?.includes("Collaborative Order Agreement") &&
        !createPatientFlow && (
          <div className="my-4">
            <CustomText text={"Path Lab"} fontWeight="font-semibold" />
            <CustomSelect
              className="!bg-sideNavGray"
              selected={selectedPathLaboratory}
              options={pathLabOptions}
              setSelected={handlePathLabSelect}
            />
          </div>
        )}
      <div className="my-4">
        <CustomText text={"Organization"} fontWeight="font-semibold" />
        <CustomSelect
          className="!bg-sideNavGray"
          selected={selectedOrg}
          options={orgOptions}
          setSelected={handleOrgSelect}
        />
      </div>
      {!createPatientFlow && !isPhysicianOnly && (
        <div>
          <CustomText text={"Clinician"} fontWeight="font-semibold" />
          <CustomSelect
            className="!bg-sideNavGray"
            selected={selectedPhy}
            options={phyOptions}
            setSelected={handlePhySelect}
          />
        </div>
      )}
      <div className="w-full flex justify-end">
        <Button
          textInformation={{ text: "next", fontWeight: "font-semibold" }}
          onClick={onNextClick}
          buttonTheme="primary"
          className="!self-end mt-4"
          disabled={
            !!selectedOrganization == false ||
            (!!selectedPhysician == false &&
              !isPhysicianOnly &&
              !createPatientFlow) ||
            (roles.includes("Collaborative Order Agreement") &&
              !!selectedPathLab === false &&
              !createPatientFlow)
          }
        />
      </div>
    </div>
  );
}

export default OrganizationAndClinicianSelection;
